// Display-only: never mutate the card or pass translated rules to the engine.
const normalize = (text) => String(text ?? '').replace(/\r\n?/g, '\n').trim();
const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const pattern = (words, flags = 'giu') => words.length
  ? new RegExp(`(?<![\\p{L}\\p{N}_])(?:${[...new Set(words)].sort((a, b) => b.length - a.length).map(escape).join('|')})(?![\\p{L}\\p{N}_])`, flags)
  : null;

// One catalog per bundled language: `language` is the package's code (the
// locale it folds and compares with), 'en' always answers with the source.
export function createRulesCatalog({ source, locale, baseline, language: code = 'de' }) {
  const fold = (text) => text.normalize('NFKC').toLocaleLowerCase(code);
  const keywordLabel = locale.terms['Keyword Ability']?.label;
  const prefixes = ['Keyword [Aa]bility', 'Schlüsselwortfähigkeit', ...(keywordLabel ? [escape(keywordLabel)] : [])].map(p => `^${p}\\.\\s*`).join('|');
  const clean = (text) => text.replace(new RegExp(prefixes, 'u'), '')
    .replace(/\(\(([^()\n]+?)\)\)/g, '$1').replace(/\)\)([^()\n]+?)\(\(/g, '$1');
  // All translations conservatively depend on the entire rules baseline. This
  // catches referenced-rule changes even when a card's own text is unchanged.
  const current = baseline ?? source.compendium;
  const fresh = Object.keys(current).length === Object.keys(source.compendium).length
    && Object.entries(current).every(([key, text]) => normalize(text) === normalize(source.compendium[key]));
  // A single-word name such as Teleport can also be an instruction. Keep its
  // rules help; protect longer names from linking individual words within them.
  const names = Object.values(source.cards).map(card => card.name).filter(name => name && /\s/.test(name));
  const namePattern = pattern(names, 'gu');
  const termMaps = { en: new Map(), [code]: new Map() };
  for (const key of Object.keys(current)) {
    termMaps.en.set(fold(key), key);
    if (key === 'Projectile') termMaps.en.set('projectiles', key);
    const term = locale.terms[key];
    if (term) for (const form of [term.label, ...(term.aliases ?? [])]) termMaps[code].set(fold(form), key);
  }
  const patterns = Object.fromEntries(Object.entries(termMaps).map(([language, map]) => [language, pattern([...map.keys()].filter(s => s.length >= 3))]));
  const byKey = new Map(Object.keys(current).map(key => [fold(key), key]));

  function entry(key, language = 'en') {
    const canonical = byKey.get(fold(key)) ?? termMaps[language]?.get(fold(key));
    if (!canonical) return null;
    const translated = language === code && fresh && Boolean(locale.compendium[canonical]?.trim());
    return {
      keyword: canonical,
      label: translated ? (locale.terms[canonical]?.label ?? canonical) : canonical,
      description: clean(translated ? locale.compendium[canonical] : current[canonical]),
      language: translated ? code : 'en',
      isKeywordAbility: /^Keyword [Aa]bility/.test(current[canonical]),
    };
  }

  return {
    fresh,
    entry,
    term(key, language = 'en') { return language === code && fresh ? (locale.terms[key]?.label ?? key) : key; },
    card(card, face = 'front', language = 'en') {
      const active = face === 'back' && card.backFace ? card.backFace : card;
      const text = active.functional_text_plain || active.functional_text || '';
      const key = `${card.unique_id ?? card.id}/${face}`;
      const candidate = locale.cards[key];
      const translated = language === code && fresh && typeof candidate === 'string'
        && (!text.trim() || Boolean(candidate.trim()))
        && normalize(source.cards[key]?.text) === normalize(text) && Boolean(source.cards[key]);
      return { text: translated ? candidate : text, language: translated ? code : 'en', translated };
    },
    entries(query = '', filter = 'all', language = 'en') {
      const q = fold(query.trim());
      const rows = Object.keys(current).map(key => entry(key, language))
        .filter(row => filter !== 'keywords' || row.isKeywordAbility)
        .sort((a, b) => a.label.localeCompare(b.label, language, { sensitivity: 'base' }));
      if (!q) return rows;
      const titles = [], bodies = [];
      for (const row of rows) {
        const aliases = locale.terms[row.keyword]?.aliases ?? [];
        if (fold([row.keyword, row.label, locale.terms[row.keyword]?.label ?? '', ...aliases].join('\n')).includes(q)) titles.push(row);
        else if (fold(`${row.description}\n${current[row.keyword]}`).includes(q)) bodies.push(row);
      }
      return [...titles, ...bodies];
    },
    segments(text, language = 'en') {
      if (!text) return [];
      const regex = patterns[language] ?? patterns.en;
      if (!regex) return [{ text }];
      const protectedRanges = namePattern ? [...text.matchAll(namePattern)].map(m => [m.index, m.index + m[0].length]) : [];
      const result = [];
      let cursor = 0;
      for (const match of text.matchAll(regex)) {
        const start = match.index, end = start + match[0].length;
        if (protectedRanges.some(([a, b]) => start < b && end > a)) continue;
        if (start > cursor) result.push({ text: text.slice(cursor, start) });
        result.push({ text: match[0], entry: entry(termMaps[language]?.get(fold(match[0])) ?? match[0], language) });
        cursor = end;
      }
      if (cursor < text.length) result.push({ text: text.slice(cursor) });
      return result;
    },
  };
}
